import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form } from "antd";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";
import UserRoles from "../../../enums/UserRoles";
import { ISkill } from "../../Users";
import { IUser } from "../index";

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (user?: IUser) => void;
  user: IUser | undefined;
}

interface IUserFormData {
  name: string;
  email: string;
  phone_number?: string;
  monthly_productive_hours?: number;
  hiring_model: "CORPORATE" | "SHAREHOLDER" | "CLT" | "CLT_TEMP";
  role: UserRoles;
  seniority: "JUNIOR" | "FULL" | "SENIOR";
  manager_id: string;
  admission_date: any;
  dismissal_date: any;
  selected_skills: {
    label: string;
    value: string;
  }[];
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const SkillModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  user,
}) => {
  const { t } = useTranslation(["consultants"]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingSkills, setLoadingSkills] = useState<boolean>(false);
  const [skills, setSkills] = useState<ISkill[]>();

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!user, [user]);

  useEffect(() => {
    setLoadingSkills(true);
    api
      .get<IUser[]>("/skills")
      .then((response) => {
        setSkills(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar os skills",
          description: "Occoreu um erro ao buscar os skills, tente novamente.",
        });
      })
      .finally(() => setLoadingSkills(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skillsOptions = useMemo(() => {
    if (!skills) {
      return undefined;
    }

    return (
      skills
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .map((skill) => ({
          label: skill.name,
          value: skill.id,
        }))
    );
  }, [skills]);

  useEffect(() => {
    form.setFieldsValue({
      ...user,
      selected_skills: user?.skills.map((skill) => ({
        label: skill.name,
        value: skill.id,
      })),
    });
  }, [form, user]);

  const handleClose = useCallback(
    (usr?: IUser) => {
      handleCloseModal(usr);
    },
    [form, handleCloseModal]
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        case 409:
          description = "O E-mail informado já é usado por outro usuário";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do usuário, tente novamente"
            : "Ocorreu um erro ao criar o usuário, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing ? "Erro na edição" : "Erro na criação",
        description,
      });
    },
    [addToast, isEditing]
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      setLoading(true);

      const { selected_skills } = data;

      const payload = {
        skills_ids: selected_skills?.map((skill) => skill.value),
      };
      try {
        const response = await api.patch(`/users/${user?.id}`, payload);
        addToast({
          type: "success",
          title: "Usuário alterado",
          description: "O usuário foi alterado com sucesso",
        });

        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleClose, isEditing, user?.id]
  );

  const handleCreateSkill = useCallback((name) => {
    api
      .post<ISkill>("/skills", { name })
      .then((response) => {
        setSkills((oldSkills) =>
          oldSkills ? [...oldSkills, response.data] : [response.data]
        );
      });
  }, []);

  return (
    <Modal
      title={t("modalAddSkills.add")}
      visible={visible}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="selected_skills"
          label={t("skills.title")}
          errors={validationErrors}
        >
          <CreatableSelect
            isMulti
            options={skillsOptions}
            isLoading={loadingSkills}
            placeholder=""
            formatCreateLabel={(input) => `Criar skill: ${input}`}
            onCreateOption={handleCreateSkill}
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: 10,
                boxShadow: "none",
                border: "1px solid #d9d9d9",
                "&:hover": {
                  border: "1px solid orange",
                },
              }),
            }}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default SkillModal;
